import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

import {
  type HotkeyItem,
  useDisclosure,
  useFocusTrap,
  useHotkeys,
  usePrevious,
} from "@mantine/hooks";

import { useIsMobile } from "@/hooks/useBreakpoint";

/** Escape closes the overlay from text fields too, not only from buttons and links. */
const ESCAPE_FROM_ANY_ELEMENT: string[] = [];

/**
 * Collapsed state shared by the desktop sidebar and the phone navigation overlay. On phones the
 * overlay covers the page, so it closes after every navigation (including one to the current
 * page), on Escape, and whenever the viewport shrinks to phone width. While it's open, focus stays
 * inside the navbar that `navbarRef` is attached to.
 */
export function useNavbarCollapse() {
  const { key: locationKey } = useLocation();
  const isMobile = useIsMobile();
  const [collapsed, { open: collapse, toggle: toggleCollapsed }] = useDisclosure(true);

  useEffect(() => {
    if (isMobile) {
      collapse();
    }
  }, [locationKey, isMobile, collapse]);

  // Mantine shows the overlay under an inclusive `max-width: sm` query, so at exactly the `sm`
  // width an expanded desktop sidebar would render full-width without the `isMobile` check.
  const isPhoneNavOpen = isMobile && !collapsed;
  const wasPhoneNavOpen = usePrevious(isPhoneNavOpen);
  const hasPhoneNavJustClosed = wasPhoneNavOpen === true && !isPhoneNavOpen;

  const navbarRef = useFocusTrap(isPhoneNavOpen);
  // Memoized so the listener rebinds only when the overlay opens or closes.
  const escapeHotkeys = useMemo<HotkeyItem[]>(
    () => [
      [
        "Escape",
        ({ target }) => {
          // As in Mantine's Modal: Escape inside an open menu or combobox closes that first.
          const isNestedOverlay =
            target instanceof HTMLElement &&
            target.getAttribute("data-mantine-stop-propagation") === "true";

          if (isPhoneNavOpen && !isNestedOverlay) {
            collapse();
          }
        },
        { preventDefault: false },
      ],
    ],
    [isPhoneNavOpen, collapse],
  );
  useHotkeys(escapeHotkeys, ESCAPE_FROM_ANY_ELEMENT);

  return { collapsed, hasPhoneNavJustClosed, isPhoneNavOpen, navbarRef, toggleCollapsed };
}
